import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Mail, Phone, MapPin, IdCard, Landmark, Users2, ShieldAlert, Calendar, Pencil, UserMinus, Camera, Trash2, Archive, ArchiveRestore } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import EmployeeForm from "@/pages/EmployeeForm";
import PhotoCropper, { fileToDataUrl } from "@/components/PhotoCropper";
import { useConfirm } from "@/components/ConfirmDialog";

function initials(name = "") {
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}
function fmt(d) { try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } }

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      {Icon && <Icon className="h-4 w-4 text-slate-400 mt-1" strokeWidth={1.5} />}
      <div className="flex-1">
        <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
        <div className="text-sm text-slate-900 mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );
}

export default function EmployeeProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [emp, setEmp] = useState(null);
  const [depts, setDepts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    const r = await api.get(`/employees/${id}`);
    setEmp(r.data);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (isReviewer) {
      Promise.all([api.get("/departments"), api.get("/employees?include_archived=true")]).then(([d, e]) => {
        setDepts(d.data);
        setEmployees(e.data.filter(x => x.id !== id));
      });
    }
  }, [isReviewer, id]);

  const archive = async () => {
    const ok = await confirm({
      tone: "warning",
      title: `Archive ${emp?.name}?`,
      description: "Archived employees are hidden from active lists but their history stays intact. You can restore them any time.",
      confirmLabel: "Archive",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/employees/${id}/archive`);
      if (data.reparented_reports > 0) toast.success(`Archived · ${data.reparented_reports} report(s) reparented`);
      else toast.success("Archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unarchive = async () => {
    try { await api.post(`/employees/${id}/unarchive`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!emp) return <div className="text-slate-500">Loading…</div>;
  const bal = emp.leave_balance || {};

  return (
    <div className="space-y-6" data-testid="employee-profile-root">
      <Link to="/employees" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to directory
      </Link>

      <Card className="p-6 rounded-xl border-slate-200/60">
        <div className="flex flex-wrap items-start gap-6">
          <AvatarWithUpload emp={emp} canEdit={isReviewer || user?.employee_id === emp.id} onChanged={load} />
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{emp.name}</h1>
              <Badge className={
                emp.status === "Active" ? "bg-emerald-50 text-emerald-700 border-0 hover:bg-emerald-50"
                : emp.status === "Notice Period" ? "bg-orange-50 text-orange-700 border-0 hover:bg-orange-50"
                : emp.status === "Exited" ? "bg-red-50 text-red-700 border-0 hover:bg-red-50"
                : "bg-slate-100 text-slate-700 border-0"
              }>{emp.status}</Badge>
              {emp.archived && (
                <Badge className="bg-slate-800 text-slate-100 border-0 hover:bg-slate-800">
                  <Archive className="h-3 w-3 mr-1" /> Archived
                </Badge>
              )}
            </div>
            <div className="text-slate-500 mt-1">{emp.designation} · {emp.department}</div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" strokeWidth={1.5} />{emp.email}</span>
              <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" strokeWidth={1.5} />{emp.phone || "—"}</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />{emp.branch}</span>
              <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />Joined {fmt(emp.joining_date)}</span>
            </div>
          </div>
          {isReviewer && (
            <div className="flex flex-col gap-2">
              <Button variant="outline" className="border-red-200 text-[#DC2626]" onClick={() => setEditOpen(true)} data-testid="edit-employee-btn">
                <Pencil className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Edit
              </Button>
              {emp.status !== "Exited" && !emp.archived && (
                <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-red-50 hover:text-[#DC2626] hover:border-red-200" onClick={() => setExitOpen(true)} data-testid="exit-employee-btn">
                  <UserMinus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Mark exit
                </Button>
              )}
              {!emp.archived ? (
                <Button variant="outline" className="border-slate-300 text-slate-500 hover:bg-slate-50" onClick={archive} data-testid="archive-employee-btn">
                  <Archive className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Archive
                </Button>
              ) : (
                <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={unarchive} data-testid="unarchive-employee-btn">
                  <ArchiveRestore className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Restore
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit {emp.name}</DialogTitle></DialogHeader>
          <EmployeeForm mode="edit" employee={emp} departments={depts} employees={employees} onDone={() => { setEditOpen(false); load(); }} />
        </DialogContent>
      </Dialog>

      <ExitDialog open={exitOpen} onClose={() => setExitOpen(false)} employee={emp} employees={employees} onDone={() => { setExitOpen(false); load(); }} />

      <Tabs defaultValue="personal">
        <TabsList>
          <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
          <TabsTrigger value="job" data-testid="tab-job">Job</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="skills" data-testid="tab-skills">Skills</TabsTrigger>
        </TabsList>
        <TabsContent value="personal">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 rounded-xl border-slate-200/60">
              <h3 className="font-display font-semibold text-slate-900 mb-2">Identity</h3>
              <Field icon={IdCard} label="Aadhaar" value={emp.aadhaar} />
              <Field icon={IdCard} label="PAN" value={emp.pan} />
              <Field icon={ShieldAlert} label="Blood group" value={emp.blood_group} />
            </Card>
            <Card className="p-6 rounded-xl border-slate-200/60">
              <h3 className="font-display font-semibold text-slate-900 mb-2">Banking & Emergency</h3>
              <Field icon={Landmark} label="Bank" value={`${emp.bank_name || "—"} · A/c ${emp.bank_account || "—"}`} />
              <Field icon={Users2} label="Emergency contact" value={`${emp.emergency_contact_name || "—"} · ${emp.emergency_contact_phone || ""}`} />
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="job">
          <Card className="p-6 rounded-xl border-slate-200/60">
            <div className="grid md:grid-cols-2 gap-x-8">
              <Field label="Employee code" value={emp.employee_code} />
              <Field label="Designation" value={emp.designation} />
              <Field label="Department" value={emp.department} />
              <Field label="Branch" value={emp.branch} />
              <Field label="Reporting manager" value={emp.reporting_manager} />
              <Field label="Joining date" value={fmt(emp.joining_date)} />
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="leave">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(bal.allotted || {}).map(([k, total]) => {
              const u = (bal.used || {})[k] || 0;
              return (
                <Card key={k} className="p-4 rounded-xl border-slate-200/60">
                  <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 capitalize">{k.replace(/_/g, " ")}</div>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="font-display text-2xl font-bold text-slate-900">{Math.max(0, total - u)}</span>
                    <span className="text-xs text-slate-500">/ {total}</span>
                  </div>
                </Card>
              );
            })}
          </div>
          <Card className="mt-4 p-6 rounded-xl border-slate-200/60">
            <h3 className="font-display font-semibold text-slate-900 mb-3">Leave history</h3>
            {emp.leaves?.length ? (
              <div className="divide-y divide-slate-100">
                {emp.leaves.map(l => (
                  <div key={l.id} className="py-3 flex justify-between text-sm">
                    <div>
                      <div className="font-semibold text-slate-900">{l.leave_type}</div>
                      <div className="text-xs text-slate-500">{fmt(l.from_date)} → {fmt(l.to_date)} · {l.days}d · {l.reason}</div>
                    </div>
                    <Badge className={
                      l.status === "Approved" ? "bg-emerald-50 text-emerald-700 border-0 hover:bg-emerald-50"
                      : l.status === "Rejected" ? "bg-red-50 text-red-700 border-0 hover:bg-red-50"
                      : "bg-orange-50 text-orange-700 border-0 hover:bg-orange-50"
                    }>{l.status}</Badge>
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-slate-500">No leave requests yet.</div>}
          </Card>
        </TabsContent>
        <TabsContent value="skills">
          <Card className="p-6 rounded-xl border-slate-200/60">
            <div className="flex flex-wrap gap-2">
              {(emp.skills || []).map(s => (
                <Badge key={s} className="bg-red-50 text-[#DC2626] border-0 hover:bg-red-50">{s}</Badge>
              ))}
              {(!emp.skills || emp.skills.length === 0) && <div className="text-sm text-slate-500">No skills listed.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ExitDialog({ open, onClose, employee, employees, onDone }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("auto"); // "auto" | "pick"
  const [newMgrId, setNewMgrId] = useState("");
  const [reason, setReason] = useState("");
  const [exitDate, setExitDate] = useState(new Date().toISOString().slice(0, 10));
  const directReports = (employees || []).filter(e => e.reporting_manager === employee.name);
  const otherActive = (employees || []).filter(e => e.status === "Active" && e.id !== employee.id);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        exit_date: exitDate,
        reason,
        new_manager_id: mode === "pick" ? (newMgrId || null) : null,
      };
      const { data } = await api.post(`/employees/${employee.id}/exit`, body);
      const rehost = data.rehost;
      if (rehost.reparented > 0) {
        toast.success(`Marked as exited. ${rehost.reparented} report(s) moved to ${rehost.new_manager || "no manager"}.`);
      } else {
        toast.success("Marked as exited.");
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="exit-dialog">
        <DialogHeader><DialogTitle>Mark {employee.name} as Exited</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          {directReports.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <div className="flex items-center gap-2 text-amber-800 font-medium mb-1"><ShieldAlert className="h-4 w-4" /> {directReports.length} direct report(s) will need a new manager</div>
              <div className="text-xs text-amber-700">{directReports.map(r => r.name).join(", ")}</div>
            </div>
          ) : (
            <div className="text-slate-500 text-xs">This employee has no direct reports — nothing to reparent.</div>
          )}

          {directReports.length > 0 && (
            <div className="space-y-2">
              <Label>Reparent reports to</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setMode("auto")} className={`h-auto p-3 rounded-lg border text-left text-xs ${mode === "auto" ? "border-[#DC2626] bg-red-50/40" : "border-slate-200"}`} data-testid="rehost-auto">
                  <div className="font-semibold mb-0.5">Auto (grand-manager)</div>
                  <div className="text-slate-500">Promote to {employee.reporting_manager || "no manager (top-level)"}</div>
                </button>
                <button type="button" onClick={() => setMode("pick")} className={`h-auto p-3 rounded-lg border text-left text-xs ${mode === "pick" ? "border-[#DC2626] bg-red-50/40" : "border-slate-200"}`} data-testid="rehost-pick">
                  <div className="font-semibold mb-0.5">Pick a new manager</div>
                  <div className="text-slate-500">Choose any active employee</div>
                </button>
              </div>
              {mode === "pick" && (
                <Select value={newMgrId} onValueChange={setNewMgrId}>
                  <SelectTrigger data-testid="pick-new-manager"><SelectValue placeholder="Select new manager…" /></SelectTrigger>
                  <SelectContent>
                    {otherActive.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.designation}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Exit date</Label><Input type="date" value={exitDate} onChange={(e) => setExitDate(e.target.value)} /></div>
          </div>
          <div><Label>Reason (optional)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Better opportunity, relocation…" /></div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (mode === "pick" && directReports.length > 0 && !newMgrId)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="confirm-exit-btn">
            {busy ? "Processing…" : "Confirm exit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function AvatarWithUpload({ emp, canEdit, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const src = emp.avatar_url ? `${API}${emp.avatar_url}` : null;
  const confirm = useConfirm();

  const pickFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files");
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8 MB (crop will resize)");
    try {
      const dataUrl = await fileToDataUrl(file);
      setCropSrc(dataUrl);
    } catch { toast.error("Could not read file"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadBlob = async (blob) => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      await api.post(`/employees/${emp.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      setCropSrc(null);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    const ok = await confirm({ tone: "danger", title: "Remove photo?", description: "Your initials will show instead.", confirmLabel: "Remove" });
    if (!ok) return;
    try { await api.delete(`/employees/${emp.id}/photo`); toast.success("Photo removed"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="relative group" data-testid="avatar-with-upload">
      <Avatar className="h-24 w-24 ring-2 ring-white shadow-md">
        {src && <AvatarImage src={src} alt={emp.name} />}
        <AvatarFallback className="bg-red-100 text-[#DC2626] text-2xl font-bold">{initials(emp.name)}</AvatarFallback>
      </Avatar>
      {canEdit && (
        <>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="photo-input" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white grid place-items-center shadow-md border-2 border-white transition-all"
            title="Change photo"
            data-testid="photo-upload-btn"
          >
            <Camera className="h-4 w-4" strokeWidth={1.8} />
          </button>
          {src && (
            <button
              type="button"
              onClick={remove}
              className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-white hover:bg-red-50 text-red-600 grid place-items-center shadow-sm border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove photo"
              data-testid="photo-remove-btn"
            >
              <Trash2 className="h-3 w-3" strokeWidth={1.8} />
            </button>
          )}
          <PhotoCropper
            open={!!cropSrc}
            imageSrc={cropSrc}
            saving={uploading}
            onCancel={() => setCropSrc(null)}
            onSave={uploadBlob}
          />
        </>
      )}
    </div>
  );
}
